// Import the new certificate generator
import {
  downloadGeneratedCertificate,
  CertificateConfig,
  DEFAULT_CERTIFICATE_TEMPLATE,
  DEFAULT_CERTIFICATE_CBT_TEMPLATE,
} from './certificateGenerator'
import { postCertificateDownloadAnalytics } from '../api/user'
import { all_routes } from '../feature-module/router/all_routes'
import {
  normalizeLeadingInteger,
  normalizeMaterialJpl,
} from './numberNormalizer'

export interface CertificateMeta {
  eventDates?: string
  jplTotal?: string
  jplNote?: string
  speakers?: string
  materials?: Array<{
    topic?: string | null
    jpl?: string | null
    speaker?: string | null
  }> | Record<string, any> | string | null
  location?: string
  signatureDate?: string
  certificateNumber?: string | null
  qrUrl?: string | null
  materialTopicOnly?: boolean
}

const normalizeCertificateMaterials = (
  materials?: CertificateMeta['materials'],
  fallbackSpeaker?: string,
): CertificateConfig['materials'] | undefined => {
  let materialRows: any[] = []

  if (Array.isArray(materials)) {
    materialRows = materials
  } else if (typeof materials === 'string') {
    try {
      const parsedMaterials = JSON.parse(materials)
      if (Array.isArray(parsedMaterials)) {
        materialRows = parsedMaterials
      } else if (parsedMaterials && typeof parsedMaterials === 'object') {
        materialRows = Object.values(parsedMaterials)
      }
    } catch {
      materialRows = materials
        .split(',')
        .map((topic) => ({ topic: topic.trim() }))
        .filter((item) => item.topic)
    }
  } else if (materials && typeof materials === 'object') {
    materialRows = Object.values(materials)
  }

  if (!materialRows.length) return undefined

  return materialRows
    .map((item) => ({
      topic:
        typeof item === 'string'
          ? item
          : item?.topic || item?.name || item?.title || '',
      jpl:
        typeof item === 'string'
          ? ''
          : normalizeMaterialJpl(item?.jpl || item?.duration || ''),
      speaker:
        typeof item === 'string'
          ? fallbackSpeaker || ''
          : item?.speaker || fallbackSpeaker || '',
    }))
    .filter((item) => item.topic || item.jpl || item.speaker)
}

const normalizeCertificateNumber = (
  certificateNumber?: string | null,
): string | undefined => {
  if (!certificateNumber) return undefined

  const parts = certificateNumber.trim().split('/')

  if (parts.length === 4 && parts[1]?.toUpperCase() === 'JP-SF') {
    const [sequence, prefix, month, year] = parts
    return `${sequence}/${prefix}/E/${month}/${year}`
  }

  if (parts.length === 5 && parts[1]?.toUpperCase() === 'JP-SF') {
    return certificateNumber.trim()
  }

  return certificateNumber.trim()
}

const buildCertificateQrValue = (
  certificateNumber?: string,
  qrUrl?: string | null,
): string | undefined => {
  if (qrUrl) return qrUrl
  if (!certificateNumber || typeof window === 'undefined') return undefined

  return `${window.location.origin}${
    all_routes.verifyCertificate
  }?number=${encodeURIComponent(certificateNumber)}`
}

export const getStoredCertificateName = (
  courseId?: number | string,
): string => {
  if (!courseId || typeof window === 'undefined') return ''
  return window.localStorage.getItem(`certificateName:${courseId}`) || ''
}

export const setStoredCertificateName = (
  courseId?: number | string,
  name?: string,
): void => {
  if (!courseId || !name?.trim() || typeof window === 'undefined') return
  window.localStorage.setItem(`certificateName:${courseId}`, name.trim())
}

export function rupiahFormatter(
  value: number | string,
  withPrefix = true,
): string {
  const number = typeof value === 'string' ? parseFloat(value) : value

  if (isNaN(number)) return withPrefix ? 'Rp0' : '0'

  return `${withPrefix ? 'Rp' : ''}${number
    .toFixed(0)
    .replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`
}

export const calculateDiscount = (
  originalPrice: number,
  discount: string,
  discountType: 'PERCENTAGE' | 'NOMINAL',
) => {
  let priceAfterPercentageDiscount = originalPrice
  const discountNominal = parseInt(discount)

  if (discountType === 'PERCENTAGE') {
    let percentageDiscountAmount = 0
    percentageDiscountAmount = originalPrice * (discountNominal / 100)
    priceAfterPercentageDiscount = originalPrice - percentageDiscountAmount
  } else if (discountType === 'NOMINAL') {
    priceAfterPercentageDiscount = originalPrice - discountNominal
  } else {
    priceAfterPercentageDiscount = originalPrice
  }

  const totalDiscountAmount = rupiahFormatter(priceAfterPercentageDiscount)

  return totalDiscountAmount
}

/**
 * Download certificate helper function
 * @param certificateName - Name of the certificate
 * @param customFileName - Optional custom filename for the certificate
 * @param pdfUrl - Optional custom PDF URL, defaults to sample certificate
 * @param userName - Optional user name for auto-generated certificate
 * @param courseTitle - Optional course title for auto-generated certificate
 * @param useAutoGeneration - Whether to use auto-generated certificate instead of static PDF
 * @param courseId - Optional course ID for analytics tracking
 * @param courseTopics - Optional course topics to display in certificate
 */

export const downloadCertificate = async (
  certificateName: string,
  customFileName?: string,
  pdfUrl: string = '/assets/certificates/sample-certificate.pdf',
  userName?: string,
  courseTitle?: string,
  useAutoGeneration: boolean = false,
  courseId?: number,
  courseTopics?: Array<{ name: string }>,
  isCBT: boolean = false,
  cbtScore: string = "",
  cbtPassingGrade: string = "",
  certificateMeta?: CertificateMeta,
): Promise<void> => {
  try {
    const parsedScore = Number(cbtScore)
    const parsedPassing = Number(cbtPassingGrade)
    const evaluationPassed =
      Number.isFinite(parsedScore) && Number.isFinite(parsedPassing)
        ? parsedScore >= parsedPassing
        : undefined
    let analyticsData: any = null

    if (courseId) {
      try {
        const analyticsResponse = await postCertificateDownloadAnalytics({
          course_id: courseId,
          name: userName || certificateName,
          evaluation_score: cbtScore || null,
          evaluation_passed:
            evaluationPassed !== undefined ? evaluationPassed : null,
          evaluation_passing_grade: cbtPassingGrade
            ? normalizeLeadingInteger(cbtPassingGrade)
            : null,
        })
        analyticsData = analyticsResponse?.data || null
      } catch (analyticsError) {
        console.error(
          'Failed to record certificate download analytics:',
          analyticsError,
        )
      }
    }

    // If auto-generation is enabled and we have user name and course title
    if (useAutoGeneration && userName && courseTitle) {
      const certificateNumber = normalizeCertificateNumber(
        analyticsData?.certificate_number || certificateMeta?.certificateNumber,
      )
      const qrValue = buildCertificateQrValue(
        certificateNumber,
        analyticsData?.qr_url || certificateMeta?.qrUrl,
      )
      const certificateEvaluationScore =
        analyticsData?.evaluation_score ?? (cbtScore || undefined)
      const certificatePassingGrade =
        analyticsData?.evaluation_passing_grade ?? (cbtPassingGrade || undefined)
      const normalizedCertificatePassingGrade =
        certificatePassingGrade !== undefined
          ? normalizeLeadingInteger(certificatePassingGrade)
          : undefined
      const certificateEvaluationPassed =
        typeof analyticsData?.evaluation_passed === 'boolean'
          ? analyticsData.evaluation_passed
          : evaluationPassed

      if (!qrValue) {
        throw new Error(
          'Nomor sertifikat belum tersedia. Silakan coba download lagi beberapa saat lagi.',
        )
      }

      const customConfig: Partial<CertificateConfig> = {
        certificateNumber,
        qrValue,
        eventDates: certificateMeta?.eventDates,
        jplTotal: certificateMeta?.jplTotal,
        jplNote: certificateMeta?.jplNote,
        speakers: certificateMeta?.speakers,
        materials: normalizeCertificateMaterials(
          certificateMeta?.materials,
          certificateMeta?.speakers,
        ),
        materialTopicOnly: certificateMeta?.materialTopicOnly,
        signature: {
          location: certificateMeta?.location,
          date: certificateMeta?.signatureDate || analyticsData?.created_at,
        },
        evaluationScore: certificateEvaluationScore,
        evaluationPassed: certificateEvaluationPassed,
        evaluationPassingGrade: normalizedCertificatePassingGrade,
      }

      await downloadCertificateAuto(
        userName,
        courseTitle,
        customFileName,
        customConfig,
        courseTopics,
        isCBT
      )
    } else {
      // Fallback to static PDF download
      const link = document.createElement('a')
      link.href = pdfUrl

      // Generate filename with current date (date only, no day name)
      const currentDate = new Date().toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      })
      const fileName =
        customFileName || `certificate-${certificateName}-${currentDate}.pdf`

      link.download = fileName
      link.style.display = 'none'

      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)

    }

  } catch (error) {
    console.error('Error downloading certificate:', error)
    if (typeof window !== 'undefined') {
      const message =
        error instanceof Error
          ? error.message
          : 'Gagal mengunduh sertifikat. Silakan coba lagi.'
      window.alert(message)
    }
  }
}

export const prepareCertificateDownload = async (
  courseId?: number,
  name?: string,
  cbtScore: string = '',
  cbtPassingGrade: string = '',
): Promise<void> => {
  if (!courseId || !name) return

  const parsedScore = Number(cbtScore)
  const parsedPassing = Number(cbtPassingGrade)
  const evaluationPassed =
    Number.isFinite(parsedScore) && Number.isFinite(parsedPassing)
      ? parsedScore >= parsedPassing
      : undefined
  const cacheKey = [
    'preparedCertificate',
    courseId,
    name,
    cbtScore,
    cbtPassingGrade,
  ].join(':')

  try {
    if (
      typeof window !== 'undefined' &&
      window.sessionStorage.getItem(cacheKey)
    ) {
      return
    }

    await postCertificateDownloadAnalytics({
      course_id: courseId,
      name,
      evaluation_score: cbtScore || null,
      evaluation_passed:
        evaluationPassed !== undefined ? evaluationPassed : null,
      evaluation_passing_grade: cbtPassingGrade
        ? normalizeLeadingInteger(cbtPassingGrade)
        : null,
    })

    if (typeof window !== 'undefined') {
      window.sessionStorage.setItem(cacheKey, '1')
    }
  } catch (error) {
    console.warn('Failed to prepare certificate QR:', error)
  }
}

/**
 * Download certificate with custom name and position
 * @param config Certificate configuration with name and position
 * @param customFileName Optional custom filename
 */
export const downloadCertificateWithPosition = async (
  config: CertificateConfig,
  customFileName?: string,
): Promise<void> => {
  try {
    await downloadGeneratedCertificate(
      config,
      DEFAULT_CERTIFICATE_TEMPLATE,
      customFileName,
    )
  } catch (error) {
    console.error('Error downloading certificate with position:', error)
    throw error
  }
}

export const buildAutoCertificateConfig = (
  userName: string,
  courseTitle: string,
  customConfig?: Partial<CertificateConfig>,
  courseTopics?: Array<{ name: string }>,
): CertificateConfig => {
  const defaultSignature = {
    signatureImageUrl: undefined,
    location: 'Bandar Lampung',
    marginBottom: 64,
    fontSize: 12,
    fontColor: { r: 0, g: 0, b: 0 },
    imageWidth: 200,
    imageHeight: 100,
    organizerText: 'Organized by PT. Jadi Praktisi Indonesia',
    showOrganizerText: false,
    signerName: 'apt. Rudi Afrinanda, S.Farm',
    signerTitle: '',
  }
  const customSignature = customConfig?.signature || {}
  const mergedSignature = { ...defaultSignature, ...customSignature }
  const { signature: _signature, ...restCustomConfig } = customConfig || {}

  return {
    name: userName,
    courseName: courseTitle,
    y: 255,
    courseY: 340,
    fontSize: 22,
    fontColor: { r: 43, g: 55, b: 82 },
    courseFontSize: 20,
    courseFontColor: { r: 43, g: 55, b: 82 },
    enableTextWrapping: true,
    maxTextWidth: 86,
    lineHeight: 1.3,
    topics: courseTopics,
    signature: mergedSignature,
    ...restCustomConfig,
  }
}

/**
 * Download certificate with auto-generated config from user name and course title
 * @param userName - Name of the user/student
 * @param courseTitle - Title of the course
 * @param customFileName - Optional custom filename for the certificate
 * @param customConfig - Optional custom configuration to override defaults
 * @param courseTopics - Optional course topics to display in certificate
 */
export const downloadCertificateAuto = async (
  userName: string,
  courseTitle: string,
  customFileName?: string,
  customConfig?: Partial<CertificateConfig>,
  courseTopics?: Array<{ name: string }>,
  isCBT: boolean = false
): Promise<void> => {
  try {
    const config = buildAutoCertificateConfig(
      userName,
      courseTitle,
      customConfig,
      courseTopics,
    )

    // Generate filename with current date (date only, no day name)
    const currentDate = new Date().toLocaleDateString('id-ID', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const fileName =
      customFileName ||
      `certificate-${userName}-${courseTitle}-${currentDate}.pdf`

    if (isCBT) {
      await downloadGeneratedCertificate(
        config,
        DEFAULT_CERTIFICATE_CBT_TEMPLATE,
        fileName,
      )
    } else {
      await downloadGeneratedCertificate(
        config,
        DEFAULT_CERTIFICATE_TEMPLATE,
        fileName,
      )
    }

  } catch (error) {
    console.error('Error downloading auto certificate:', error)
    throw error
  }
}
